import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, categoryProducts } from '@/mocks/categories';
import type { Category } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || 'smart-solutions');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const products = categoryProducts[activeSlug || 'smart-solutions'] || [];

  const handleCategoryClick = (cat: Category) => {
    setActiveSlug(cat.slug);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-28 pb-10 md:pt-32 md:pb-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">分类浏览</span>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-900">{activeCategory.name}</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
              {activeCategory.name}
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl leading-relaxed">
              {activeCategory.description}
            </p>
          </div>
        </section>

        <section className="py-0 pb-6 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => handleCategoryClick(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            {products.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无产品</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="group rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed mb-4 line-clamp-2">
                        {product.description}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {product.features.map((f) => (
                          <span
                            key={f}
                            className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}